import React, { useEffect, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';

import { postLogoutRoute } from 'src/routing/routes';
import sessionManagerSingleton from '../../services/session-management.service';
import { logout, expiredSessionLogout } from '../../services/user.service';

import { ProgressBar, Modal } from 'src/components';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import useIntervalWebWorker from 'src/hooks/use-interval-web-worker';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import './session-management.scss';

export default function SessionManagement() {
  const [showWarning, setShowWarning] = useState(false);
  const [timerValue, setTimerValue] = useState(30);
  const [loggedOut, setLoggedOut] = useState(false);
  const infoTrayRef = useRef();
  const WARNING_DURATION = 30000;
  const handleTechnicalError = useTechnicalErrorHandler(true);
  const [setInterval, clearInterval] = useIntervalWebWorker();

  const onLogoutClickedOrTimedOut = (killSessionWithNoError = false) => {
    if (!killSessionWithNoError) {
      // don't call clear interval twice if user clicked on continue and manage session returned an error
      clearInterval();
    }
    toastMessageServiceSingleton.removeAllToasts();
    killSessionWithNoError ? expiredSessionLogout() : logout(handleTechnicalError, true);
    setLoggedOut(true);
  };

  const onContinueOrOutsideClicked = () => {
    setShowWarning(false);
    setTimerValue(WARNING_DURATION / 1000);
    clearInterval();
    if (!sessionManagerSingleton.extendSession(true)) {
      onLogoutClickedOrTimedOut(true);
    }
    sessionManagerSingleton.initiateTimer();
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (infoTrayRef.current && infoTrayRef.current.contains(e.target)) {
        // inside click
        return;
      }
      // outside click
      if (showWarning) {
        onContinueOrOutsideClicked();
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  });

  useEffect(() => {
    sessionManagerSingleton.initiateTimer();
    sessionManagerSingleton.setHandleErrorMethod(handleTechnicalError);

    sessionManagerSingleton.setWarningMethod(() => {
      setShowWarning(true);
      let timeLeft = WARNING_DURATION / 1000;

      setInterval(() => {
        setTimerValue(timeLeft - 1);
        timeLeft--;

        if (timeLeft === 0) {
          onLogoutClickedOrTimedOut();
        }
      }, 1000);
    });

    sessionManagerSingleton.setLogoutMethod((isExpiredSession) => {
      isExpiredSession ? expiredSessionLogout() : logout(handleTechnicalError, true);
      setLoggedOut(true);
    });

    return () => {
      sessionManagerSingleton.unloadSessionManager();
    };
  }, []);

  if (loggedOut) {
    return <Redirect to={postLogoutRoute} />;
  }

  const modalTitle = () => (
    <div>
      <h3 className="modal-title">Are you still There?</h3>
      <h4 className="modal-subtitle">Your online session will logout in...</h4>
    </div>
  );

  const modalBody = () => (
    <div className="timer-box">
      <div className="timer-value">{timerValue}</div>
      <p className="timer-subtitle">Seconds</p>
      <div>
        <ProgressBar progress={(timerValue / (WARNING_DURATION / 1000)) * 100} />
      </div>
    </div>
  );

  const modalFooter = () => (
    <div className="columns">
      <div className="column">
        <button
          className="button sign-out-button is-fullwidth"
          onClick={() => {
            onLogoutClickedOrTimedOut();
          }}
        >
          Sign out
        </button>
      </div>
      <div className="column">
        <button
          className="button continue-button is-fullwidth"
          onClick={() => {
            onContinueOrOutsideClicked();
          }}
        >
          Continue with session
        </button>
      </div>
    </div>
  );

  return (
    <div ref={infoTrayRef} className="SessionManagement">
      <Modal
        modalStyle="SubmitModal"
        show={showWarning}
        cardStyle="session-management-modal-card"
        header={modalTitle}
        headerStyle="session-management-modal-card-head has-text-centered"
        body={modalBody}
        bodyStyle="session-management-modal-card-body has-text-centered"
        footer={modalFooter}
        footerStyle="session-management-modal-footer"
        backdropStyle="backdrop"
      />
    </div>
  );
}
